import type React from 'react';
import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';

export interface NotificationItem {
  id: string;
  heading?: React.ReactNode;
  icon?: React.ReactNode;
  message?: React.ReactNode;
  isOpen: boolean;
  type: 'default' | 'error' | 'success';
}

interface NotificationStore {
  notifications: NotificationItem[];

  addNotification: (
    options: {
      autoDismiss?: boolean;
    } & Partial<Pick<NotificationItem, 'heading' | 'icon' | 'message' | 'type'>>
  ) => void;

  removeNotification: (id: string) => void;
}

export const useNotificationStore = create(
  subscribeWithSelector<NotificationStore>((set, get) => ({
    notifications: [],

    addNotification: ({ autoDismiss = true, heading, icon, message, type = 'default' }) => {
      const id = `${Date.now()}-${Math.ceil(Math.random() * 100)}`;

      const notification: NotificationItem = {
        id,
        heading,
        icon,
        message,
        type,
        isOpen: true
      };

      set(state => ({
        ...state,
        notifications: [...state.notifications, notification]
      }));

      // Auto dismiss notification after 10 seconds
      if (autoDismiss) {
        // Update `isOpen` and delay the removal to allow for exit animations
        window.setTimeout(() => {
          set(state => {
            const index = state.notifications.findIndex(n => n.id === id);

            if (index === -1) {
              return state;
            }

            const updatedNotification = {
              ...state.notifications[index],
              isOpen: false
            };

            return {
              ...state,
              notifications: [
                ...state.notifications.slice(0, index),
                updatedNotification,
                ...state.notifications.slice(index + 1)
              ]
            };
          });
        }, 10_000);

        // Wait 1 second after setting `isOpen` to false before actually
        // removing the notification
        window.setTimeout(() => {
          get().removeNotification(id);
        }, 11_000);
      }
    },

    removeNotification: (id: string) => {
      set(state => ({
        ...state,
        notifications: state.notifications.filter(n => n.id !== id)
      }));
    }
  }))
);

export const useCreateNotification = () => useNotificationStore().addNotification;
